import React, { useState, useMemo, useCallback } from "react";
import { ChevronDown, ChevronRight, Calendar, Layers } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { toVnDateString } from "@apv-erp/status";
export interface DateGroup<T> {
  dateKey: string;
  formattedDate: string;
  items: T[];
  count: number;
  isCollapsed: boolean;
  toggleCollapse: () => void;
  isToday: boolean;
  isYesterday: boolean;
}

export interface DateGroupedTableProps<T> {
  items: T[];
  getDateKey: (item: T) => string;
  formatDateLabel?: (dateKey: string) => string;
  isGrouped?: boolean;
  renderRow?: (item: T, index: number, isLastInGroup: boolean) => React.ReactNode;
  renderItem?: (item: T, index: number, isLastInGroup: boolean) => React.ReactNode;
  renderGroupHeader: (group: DateGroup<T>) => React.ReactNode;
  renderPageSummary?: (items: T[]) => React.ReactNode;
  defaultCollapsed?: boolean;
  emptyState?: React.ReactNode;
  /** Thứ tự nhóm ngày: mới → cũ (mặc định) hoặc cũ → mới (VD bảng theo hạn giao: hạn gần nhất lên trước) */
  sortOrder?: "desc" | "asc";
}

/**
 * Format a date string YYYY-MM-DD into a friendly Vietnamese label:
 * - "Hôm nay • 30/09/2026"
 * - "Hôm qua • 29/09/2026"
 * - "Thứ Hai, 28/09/2026"
 */
export function defaultFormatDateLabel(dateKey: string): string {
  if (!dateKey || dateKey === "UNKNOWN" || dateKey === "Chưa xác định") {
    return "Chưa xác định ngày";
  }

  try {
    const parts = dateKey.split("-");
    if (parts.length !== 3) return dateKey;

    const [year, month, day] = parts.map(Number);
    const dateObj = new Date(year, month - 1, day);
    if (isNaN(dateObj.getTime())) return dateKey;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const targetDay = new Date(year, month - 1, day);
    const formattedDmy = `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;

    if (targetDay.getTime() === today.getTime()) {
      return `Hôm nay • ${formattedDmy}`;
    }
    if (targetDay.getTime() === yesterday.getTime()) {
      return `Hôm qua • ${formattedDmy}`;
    }

    const dayOfWeek = targetDay.getDay();
    const daysMap = [
      "Chủ Nhật",
      "Thứ Hai",
      "Thứ Ba",
      "Thứ Tư",
      "Thứ Năm",
      "Thứ Sáu",
      "Thứ Bảy",
    ];
    return `${daysMap[dayOfWeek]}, ${formattedDmy}`;
  } catch {
    return dateKey;
  }
}

/**
 * Extract YYYY-MM-DD key from ISO string, Date object or null
 */
export function extractDateKey(val: any): string {
  if (!val) return "UNKNOWN";
  try {
    if (val instanceof Date) {
      if (isNaN(val.getTime())) return "UNKNOWN";
      const y = val.getFullYear();
      const m = String(val.getMonth() + 1).padStart(2, "0");
      const d = String(val.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }
    const str = String(val);
    // Ngày thuần 'YYYY-MM-DD' giữ nguyên; chuỗi ISO có giờ (UTC) đổi về ngày lịch Việt Nam
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    if (str.length >= 10 && str.charAt(4) === "-" && str.charAt(7) === "-") {
      const vn = toVnDateString(str);
      if (vn) return vn;
    }
    const d = new Date(str);
    if (isNaN(d.getTime())) return "UNKNOWN";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return "UNKNOWN";
  }
}

/**
 * Reusable DateGroupedTable component for ERP list pages
 * Handles grouping by date with in-row subtotals, collapse/expand, and page summaries
 */
/** Đánh dấu dòng nhóm và dòng tổng trang (data-group-row) để kiểm tra chung: dòng nhóm + tổng phụ luôn nằm trên một dòng */
function withGroupRowMarker(node: React.ReactNode): React.ReactNode {
  return React.isValidElement(node) ? React.cloneElement(node as React.ReactElement<any>, { "data-group-row": "" }) : node;
}

export function DateGroupedTable<T>({
  items,
  getDateKey,
  formatDateLabel = defaultFormatDateLabel,
  isGrouped = true,
  renderRow,
  renderItem,
  renderGroupHeader,
  renderPageSummary,
  defaultCollapsed = false,
  emptyState,
  sortOrder = "desc",
}: DateGroupedTableProps<T>) {
  const [collapsedDates, setCollapsedDates] = useState<Set<string>>(() => {
    return defaultCollapsed ? new Set() : new Set();
  });

  const toggleCollapse = useCallback((dateKey: string) => {
    setCollapsedDates((prev) => {
      const next = new Set(prev);
      if (next.has(dateKey)) {
        next.delete(dateKey);
      } else {
        next.add(dateKey);
      }
      return next;
    });
  }, []);

  // Canonical row renderer: supports both renderRow and renderItem aliases
  const rowRenderer = renderRow || renderItem;

  // Compute date groups in O(N)
  const groups: DateGroup<T>[] = useMemo(() => {
    if (!items || items.length === 0) return [];

    const map = new Map<string, T[]>();
    for (const item of items) {
      const key = getDateKey(item) || "UNKNOWN";
      const list = map.get(key);
      if (list) {
        list.push(item);
      } else {
        map.set(key, [item]);
      }
    }

    // Sort date keys descending: today -> past -> UNKNOWN at the end
    const sortedKeys = Array.from(map.keys()).sort((a, b) => {
      if (a === "UNKNOWN") return 1;
      if (b === "UNKNOWN") return -1;
      return sortOrder === "asc" ? a.localeCompare(b) : b.localeCompare(a);
    });

    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const yest = new Date(now);
    yest.setDate(yest.getDate() - 1);
    const yesterdayStr = `${yest.getFullYear()}-${String(yest.getMonth() + 1).padStart(2, "0")}-${String(yest.getDate()).padStart(2, "0")}`;

    return sortedKeys.map((key) => {
      const groupItems = map.get(key)!;
      return {
        dateKey: key,
        formattedDate: formatDateLabel(key),
        items: groupItems,
        count: groupItems.length,
        isCollapsed: collapsedDates.has(key),
        toggleCollapse: () => toggleCollapse(key),
        isToday: key === todayStr,
        isYesterday: key === yesterdayStr,
      };
    });
  }, [items, getDateKey, formatDateLabel, collapsedDates, toggleCollapse, sortOrder]);

  if (!items || items.length === 0) {
    return emptyState ? <>{emptyState}</> : null;
  }

  // Defensive guard: if no row renderer is supplied, safely return empty
  if (typeof rowRenderer !== "function") {
    return null;
  }

  // Flat rendering when grouping is turned off
  if (!isGrouped) {
    return (
      <>
        {items.map((item, idx) => rowRenderer(item, idx, idx === items.length - 1))}
        {renderPageSummary && withGroupRowMarker(renderPageSummary(items))}
      </>
    );
  }

  // Grouped rendering by date
  return (
    <>
      {groups.map((group) => (
        <React.Fragment key={group.dateKey}>
          {withGroupRowMarker(renderGroupHeader(group))}
          {!group.isCollapsed &&
            group.items.map((item, idx) =>
              rowRenderer(item, idx, idx === group.items.length - 1)
            )}
        </React.Fragment>
      ))}
      {renderPageSummary && withGroupRowMarker(renderPageSummary(items))}
    </>
  );
}

/**
 * Standard Toggle Button to switch Date Grouping on/off in the toolbar
 */
export function DateGroupToggleButton({
  isGrouped,
  onToggle,
  className,
}: {
  isGrouped: boolean;
  onToggle: () => void;
  className?: string;
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onToggle}
      className={cn(
        "h-8 px-2.5 text-xs font-medium transition-all gap-1.5",
        isGrouped
          ? "bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100 hover:text-indigo-800 font-semibold shadow-xs"
          : "text-slate-600 border-slate-200 hover:bg-slate-100 hover:text-slate-900",
        className
      )}
      title={isGrouped ? "Đang gom nhóm theo ngày (Click để xem danh sách phẳng)" : "Đang xem phẳng (Click để gom nhóm theo ngày)"}
    >
      <Layers className={cn("h-3.5 w-3.5", isGrouped ? "text-indigo-600" : "text-slate-500")} />
      <span>{isGrouped ? "Gom nhóm: Bật" : "Gom nhóm: Tắt"}</span>
    </Button>
  );
}
